import { ChannelType, getChannelTypePicture } from "../../../../src/models/ChannelType";
import type { C1_FurnitureDefinition, C1_FurnitureRendererProps } from "../../types/C1_FurnitureTypes";
import { FurnitureCategory } from "../../types/C1_ModelTypes";
import { C1_FurniturePlacementPreviewContext, C1_FurnitureSvg } from "../C1_FurnitureSvg";
import c1_state from "../../model/C1_State";
import { useOnChangeWatcher } from "../../../../src/models/useOnChangeWatcher";
import { ButtonFrontView } from "../../frontViews/ButtonFrontview";
import { WallOutletSvg, type WallOutletSlotType } from "../../frontViews/WallOutletFrontview";
import { getButtonProductDefinition, getLightProductImageUrl } from "../../data/C1_ProductDefinitions";
import {
  C1_EloItem_PlugType,
  C1_EloItem_Type,
  type C1_ButtonArea,
  type C1_EloItem_Data,
} from "../../types/C1_EloTypes";
import type { RockerRowConfig } from "../../frontViews/GlasButtonFrontView";
import { FixedPowerFrontView } from "../../frontViews/FixedPowerFrontView";
import { CameraFrontView } from "../../frontViews/CameraFrontView";
import { useContext, type ReactElement } from "react";
import { C1_ELO_PLAN_MODULE_SIZE } from "../../lib/C1_planSymbolSizes";
import cameraIconUrl from "../../data/others/camera.svg";
import motionSensorIconUrl from "../../data/others/motion-sensor.svg";
import smokeSensorIconUrl from "../../data/others/smoke-sensor.svg";
import { LightPaletteIcon } from "./C1_LightPaletteIcon";

const WALL_ITEM_SIZE = 0.16;

type AdditionalElectricalKind = "contact" | "sensor" | "motion" | "smoke" | "jalousie" | "blind" | "doorStation" | "distribution";

function AdditionalElectricalIcon({ kind }: { kind: AdditionalElectricalKind }) {
  if (kind === "distribution") return <image href={getChannelTypePicture(ChannelType.IntermediateDistribution)} x={2} y={2} width={20} height={20} />;
  if (kind === "blind") return <g fill="none" stroke="currentColor" strokeWidth={1.2} strokeLinecap="round">
    <rect x={4} y={3} width={16} height={18} />
    <path d="M4 6 H20 M4 17 H20 M22 5 V17" /><circle cx={22} cy={18} r={1} />
  </g>;
  if (kind === "contact") return <image href={state.hostBaseUrl + "images/consumer/InputSwitch.png"} x={2} y={2} width={20} height={20} />;
  if (kind === "motion" || kind === "smoke") return <image href={kind === "motion" ? motionSensorIconUrl : smokeSensorIconUrl} x={2} y={2} width={20} height={20} />;
  if (kind === "doorStation") return <image href={state.hostBaseUrl + "images/consumer/DoorStation.png"} x={2} y={2} width={20} height={20} />;
  if (kind === "sensor") return <g fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round">
    <circle cx={12} cy={12} r={3} /><path d="M7 7a7 7 0 0 0 0 10M17 7a7 7 0 0 1 0 10M4 4a11 11 0 0 0 0 16M20 4a11 11 0 0 1 0 16" />
  </g>;
  return <g fill="none" stroke="currentColor" strokeWidth={1.2} strokeLinecap="round">
    <rect x={4} y={3} width={16} height={18} />
    <path d="M4 7 H20 M4 11 H20 M4 15 H20 M4 19 H20 M22 5 V17" /><circle cx={22} cy={18} r={1} />
  </g>;
}

/** Palette definitions use the shared standalone-device creation path. */
function additionalElectricalItem(id: string, de: string, en: string, kind: AdditionalElectricalKind): C1_FurnitureDefinition {
  return {
    id, description: { de, en }, category: FurnitureCategory.Electrical,
    defaultWidth: WALL_ITEM_SIZE, defaultHeight: WALL_ITEM_SIZE,
    originXPercentage: 50, originYPercentage: 0, autoRotate: true,
    editableGeometry: false, properties: [],
    icon: <svg viewBox="0 0 24 24" aria-hidden="true"><AdditionalElectricalIcon kind={kind} /></svg>,
    Renderer: props => <ElectricalFurnitureSymbol>
      <SymbolSvg {...props} fixedPreviewSize={23.75}>
        {kind === "doorStation" && <circle cx={0} cy={0} r={12}
          fill={props.strokeColor === "orange" ? "#f59e0b" : props.strokeColor === "#ffc766" ? "#fdba74" : "#ffffff"}
          fillOpacity={0.5}
          stroke={props.strokeColor === "orange" ? "#f59e0b" : props.strokeColor === "#ffc766" ? "#fdba74" : "#94a3b8"}
          strokeWidth={props.strokeColor === "orange" ? 1.4 : props.strokeColor === "#ffc766" ? 1.1 : 0.6} />}
        <g transform={kind === "doorStation" ? "translate(-6 -6) scale(0.5)" : "translate(-12 -12)"}>
          <AdditionalElectricalIcon kind={kind} />
        </g>
      </SymbolSvg>
    </ElectricalFurnitureSymbol>,
  };
}

export const ElectricalWindowContact = additionalElectricalItem("electricalWindowContact", "Fensterkontakt", "Window contact", "contact");
export const ElectricalMotionSensor = additionalElectricalItem("electricalMotionSensor", "Bewegungsmelder", "Motion detector", "motion");
export const ElectricalSmokeSensor = additionalElectricalItem("electricalSmokeSensor", "Rauchmelder", "Smoke detector", "smoke");
export const ElectricalSensor = additionalElectricalItem("electricalSensor", "Sensor allgemein", "General sensor", "sensor");
export const ElectricalDoorStation = additionalElectricalItem("electricalDoorStation", "Türstation", "Door station", "doorStation");
export const ElectricalJalousie = additionalElectricalItem("electricalJalousie", "Jalousie", "Venetian blind", "jalousie");
export const ElectricalBlind = additionalElectricalItem("electricalBlind", "Rollo", "Roller blind", "blind");
export const ElectricalDistribution = additionalElectricalItem("electricalDistribution", "Unterverteiler", "Subdistribution", "distribution");

function StandardButtonIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <image href={state.hostBaseUrl + "images/buttons/touch-6.png"}
        x="5" y="4" width="14" height="16" preserveAspectRatio="xMidYMid meet" />
    </svg>
  );
}

function ElectricalFurnitureSymbol({ children }: { children: ReactElement }): ReactElement | null {
  useOnChangeWatcher(c1_state.editMode);
  const placementPreview = useContext(C1_FurniturePlacementPreviewContext);
  // Im Elektro-Modus übernimmt der zentrale ELO-Layer mit Tag und Interaktion.
  // In allen anderen Modi bleibt das Symbol Teil des Möbels und damit sichtbar.
  return (c1_state.editMode.value === "EloConcept" || c1_state.editMode.value === "EloDetail") && !placementPreview ? null : children;
}

function onlyEloItem(instance: C1_EloItem_Data | undefined): C1_EloItem_Data | undefined {
  return instance?.items[0];
}

function buttonRows(item: C1_EloItem_Data | undefined): RockerRowConfig[] {
  if (item?.properties?.type !== C1_EloItem_Type.Button) return [];
  const displayFunction = (area: C1_ButtonArea) => {
    const channel = area.shortPress ?? area.longPress ?? area.veryLongPress;
    return channel ? { channelType: ChannelType.Switch, name: channel.name } : undefined;
  };
  return (item.properties.rows ?? []).map((row) =>
    row.buttons.length === 1
      ? { mode: "shared", shared: displayFunction(row.buttons[0]) }
      : { mode: "split", left: displayFunction(row.buttons[0]), right: displayFunction(row.buttons[1]) },
  );
}

function outletSlot(item: C1_EloItem_Data | undefined): WallOutletSlotType {
  const type = item?.details?.plugType;
  if (type === C1_EloItem_PlugType.LAN) return "lan";
  if (type === C1_EloItem_PlugType.LAN2) return "lan2";
  if (type === C1_EloItem_PlugType.TV) return "tv";
  if (type === C1_EloItem_PlugType.USB) return "usb";
  if (type?.startsWith(C1_EloItem_PlugType.V230Switch)) return "schukoSwitched";
  return "schuko";
}

function outletSlots(instance: C1_EloItem_Data | undefined): WallOutletSlotType[] {
  const root = onlyEloItem(instance);
  const plugs = root?.items.length ? root.items : root ? [root] : [];
  return plugs.map(outletSlot);
}

function SymbolSvg({
  children,
  width,
  height,
  fixedPreviewSize,
  ...props
}: C1_FurnitureRendererProps & { children: React.ReactNode; fixedPreviewSize?: number }) {
  const placementPreview = useContext(C1_FurniturePlacementPreviewContext);
  const renderedWidth = placementPreview && fixedPreviewSize ? fixedPreviewSize : width;
  const renderedHeight = placementPreview && fixedPreviewSize ? fixedPreviewSize : height;
  return (
    <C1_FurnitureSvg
      {...props}
      width={renderedWidth}
      height={renderedHeight}
      viewBoxX={-12}
      viewBoxY={-12}
      viewBoxWidth={24}
      viewBoxHeight={24}
      originXPercentage={50}
      originYPercentage={0}
    >
      {children}
    </C1_FurnitureSvg>
  );
}

export const ElectricalCamera: C1_FurnitureDefinition = {
  id: "electricalCamera",
  description: { de: "Kamera", en: "Camera" },
  category: FurnitureCategory.Electrical,
  defaultWidth: WALL_ITEM_SIZE,
  defaultHeight: WALL_ITEM_SIZE,
  originXPercentage: 50,
  originYPercentage: 0,
  autoRotate: true,
  editableGeometry: false,
  properties: [],
  icon: <img src={cameraIconUrl} alt="" style={{ width: "70%", alignSelf: "center" }} />,
  Renderer: (props) => (
    <ElectricalFurnitureSymbol>
      <SymbolSvg {...props} fixedPreviewSize={23.75}>
        <CameraFrontView x={-12} y={-12} width={24} height={24} />
      </SymbolSvg>
    </ElectricalFurnitureSymbol>
  ),
};

export const ElectricalButton: C1_FurnitureDefinition = {
  id: "electricalButton",
  description: { de: "Taster", en: "Button" },
  category: FurnitureCategory.Electrical,
  defaultWidth: WALL_ITEM_SIZE,
  defaultHeight: WALL_ITEM_SIZE,
  originXPercentage: 50,
  originYPercentage: 0,
  autoRotate: true,
  editableGeometry: false,
  properties: [],
  icon: <StandardButtonIcon />,
  Renderer: (props) => {
    const item = onlyEloItem(props.eloTree);
    const product = getButtonProductDefinition(item?.details?.buttonType);
    return (
      <ElectricalFurnitureSymbol>
        <SymbolSvg {...props} fixedPreviewSize={23.75}>
          <ButtonFrontView
            uiTemplate={product?.uiTemplate ?? "simple"}
            rows={buttonRows(item)}
            color={item?.properties?.type === C1_EloItem_Type.Button ? item.properties.color : "white"}
            orientation={item?.properties?.type === C1_EloItem_Type.Button ? item.properties.orientation : "vertical"}
            hasDisplay={item?.properties?.type === C1_EloItem_Type.Button ? item.properties.hasDisplay : false}
            x={-9.5}
            y={-9.5}
            width={19}
            height={19}
            usePhysicalSize={false}
          />
        </SymbolSvg>
      </ElectricalFurnitureSymbol>
    );
  },
};

export const ElectricalOutlet: C1_FurnitureDefinition = {
  id: "electricalOutlet",
  description: { de: "Steckdosengruppe", en: "Socket group" },
  category: FurnitureCategory.Electrical,
  defaultWidth: 0.32,
  defaultHeight: WALL_ITEM_SIZE,
  originXPercentage: 50,
  originYPercentage: 0,
  autoRotate: true,
  editableGeometry: false,
  properties: [],
  icon: (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="2" y="6.5" width="20" height="11" rx="1.5" fill="none" stroke="currentColor" strokeWidth="0.85" />
      {[7, 17].map(x => (
        <g key={x}>
          <circle cx={x} cy="12" r="3.4" fill="none" stroke="currentColor" strokeWidth="0.85" />
          <circle cx={x - 1.3} cy="12" r="0.45" fill="currentColor" />
          <circle cx={x + 1.3} cy="12" r="0.45" fill="currentColor" />
          <path d={`M${x} 8.6v1.1M${x} 14.3v1.1`} fill="none" stroke="currentColor" strokeWidth="0.85" strokeLinecap="round" />
        </g>
      ))}
    </svg>
  ),
  Renderer: (props) => {
    const slots = outletSlots(props.eloTree);
    const renderedSlots: WallOutletSlotType[] = slots.length ? slots : ["schuko", "schuko"];
    const width = renderedSlots.length * C1_ELO_PLAN_MODULE_SIZE;
    return (
      <ElectricalFurnitureSymbol>
        <C1_FurnitureSvg
          {...props}
          width={width}
          height={C1_ELO_PLAN_MODULE_SIZE}
          viewBoxX={-width / 2}
          viewBoxY={-C1_ELO_PLAN_MODULE_SIZE / 2}
          viewBoxWidth={width}
          viewBoxHeight={C1_ELO_PLAN_MODULE_SIZE}
          originXPercentage={50}
          originYPercentage={0}
        >
          <WallOutletSvg
            slots={renderedSlots}
            x={-width / 2}
            y={-C1_ELO_PLAN_MODULE_SIZE / 2}
            width={width}
            height={C1_ELO_PLAN_MODULE_SIZE}
          />
        </C1_FurnitureSvg>
      </ElectricalFurnitureSymbol>
    );
  },
};

export const OutdoorButton: C1_FurnitureDefinition = {
  ...ElectricalButton,
  id: "outdoorButton",
  category: FurnitureCategory.Outdoor,
};

export const OutdoorOutletGroup: C1_FurnitureDefinition = {
  ...ElectricalOutlet,
  id: "outdoorOutletGroup",
  description: { de: "Steckdosen (Gruppe)", en: "Sockets (Group)" },
  category: FurnitureCategory.Outdoor,
  defaultWidth: 0.32,
  Renderer: (props) => {
    const slots = outletSlots(props.eloTree);
    const renderedSlots: WallOutletSlotType[] = slots.length ? slots : ["schuko", "schuko"];
    const width = renderedSlots.length * C1_ELO_PLAN_MODULE_SIZE;
    return (
      <ElectricalFurnitureSymbol>
        <C1_FurnitureSvg
          {...props}
          width={width}
          height={C1_ELO_PLAN_MODULE_SIZE}
          viewBoxX={-width / 2}
          viewBoxY={-C1_ELO_PLAN_MODULE_SIZE / 2}
          viewBoxWidth={width}
          viewBoxHeight={C1_ELO_PLAN_MODULE_SIZE}
          originXPercentage={50}
          originYPercentage={0}
        >
          <WallOutletSvg
            slots={renderedSlots}
            x={-width / 2}
            y={-C1_ELO_PLAN_MODULE_SIZE / 2}
            width={width}
            height={C1_ELO_PLAN_MODULE_SIZE}
          />
        </C1_FurnitureSvg>
      </ElectricalFurnitureSymbol>
    );
  },
};

export const OutdoorLight: C1_FurnitureDefinition = {
  id: "outdoorLight",
  description: { de: "Leuchte", en: "Light" },
  category: FurnitureCategory.Outdoor,
  defaultWidth: 0.32,
  defaultHeight: 0.32,
  originXPercentage: 50,
  originYPercentage: 50,
  autoRotate: true,
  editableGeometry: false,
  properties: [],
  icon: <LightPaletteIcon />,
  Renderer: (props) => {
    useOnChangeWatcher(c1_state.floorImageRenderOptions);
    const imageUrl = c1_state.floorImageRenderOptions.value?.excludeEloImages
      ? undefined
      : getLightProductImageUrl(onlyEloItem(props.eloTree)?.details?.lightProductId);
    return (
      <ElectricalFurnitureSymbol>
        <C1_FurnitureSvg
          {...props}
          viewBoxX={-16}
          viewBoxY={-16}
          viewBoxWidth={32}
          viewBoxHeight={32}
          originXPercentage={50}
          originYPercentage={50}
        >
          {imageUrl ? (
            <image href={imageUrl} x={-16} y={-16} width={32} height={32} preserveAspectRatio="xMidYMid meet" />
          ) : (
            <circle cx={0} cy={0} r={10} fill="#fff" stroke="#173f56" strokeWidth={2} />
          )}
        </C1_FurnitureSvg>
      </ElectricalFurnitureSymbol>
    );
  },
};

export const ElectricalLight: C1_FurnitureDefinition = {
  ...OutdoorLight,
  id: "electricalLight",
  description: { de: "Leuchte", en: "Light" },
  category: FurnitureCategory.Electrical,
};

function FixedPowerPaletteIcon({ voltage }: { voltage: "230" | "400" }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="5" y="6" width="14" height="12" fill="none" stroke="currentColor" strokeWidth="1" />
      <text x="12" y="12.5" textAnchor="middle" dominantBaseline="middle"
        fill="currentColor" fontFamily="Arial, sans-serif" fontSize="5.5" fontWeight="700">{voltage}</text>
    </svg>
  );
}

function createFixedConsumer(defaultVoltage: "230V" | "400V"): C1_FurnitureDefinition {
  return {
    id: defaultVoltage === "400V" ? "fixedConsumer400" : "fixedConsumer",
    description: { de: `Feststrom ${defaultVoltage}`, en: `Fixed power ${defaultVoltage}` },
    category: FurnitureCategory.Electrical,
    defaultWidth: 0.32,
    defaultHeight: 0.44,
    originXPercentage: 50,
    // Der Andock-/Drehpunkt liegt im Quadrat; das Kabel darf darunter frei herauslaufen.
    originYPercentage: 36.364,
    autoRotate: true,
    editableGeometry: false,
    properties: [],
    icon: <FixedPowerPaletteIcon voltage={defaultVoltage === "400V" ? "400" : "230"} />,
    Renderer: (props) => {
      const voltage = onlyEloItem(props.eloTree)?.details?.voltage ?? defaultVoltage;
      return (
        <ElectricalFurnitureSymbol>
          <C1_FurnitureSvg
            {...props}
            viewBoxX={-16}
            viewBoxY={-16}
            viewBoxWidth={32}
            viewBoxHeight={44}
            originXPercentage={50}
            originYPercentage={36.364}
          >
            <g color="#173f56">
              <FixedPowerFrontView
                voltage={voltage}
                idPrefix={`c1-fixed-consumer-${props.id}`}
                contentRotation={Number(props.ownerRotation)}
                contentReflected={Boolean(props.ownerReflect)}
              />
            </g>
          </C1_FurnitureSvg>
        </ElectricalFurnitureSymbol>
      );
    },
  };
}

export const FixedConsumer = createFixedConsumer("230V");
export const FixedConsumer400 = createFixedConsumer("400V");
